import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { setCookies, deleteCookie, getCookie } from 'cookies-next';

import { setItem, removeItem, KEYS } from '@utils/cache';

export enum BANNER_TARGETS {
  HOMEPAGE = 'homepage',
  INTERNSHIP = 'internship',
  CANDIDATE = 'candidate',
  EMPLOYER = 'employer',
  COVER = 'cover',
}

const apiVersion = 'v1';

const config: AxiosRequestConfig = {
  baseURL: `${process.env['NEXT_PUBLIC_API_URL']}/api/${apiVersion}`,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
};

export const API: AxiosInstance = axios.create(config);

type BannerTarget = BANNER_TARGETS;

const cookieToken = getCookie(KEYS.AUTH_TOKEN);

if (cookieToken) {
  API.defaults.headers.common['authorization'] = cookieToken;
}

export const setApiLocale = (locale: string) => {
  API.defaults.headers.common['Accept-Language'] = locale;
};

export const setApiAuthHeaders: (token: string) => void = (token) => {
  API.defaults.headers.common['authorization'] = token;
  setItem(KEYS.AUTH_TOKEN, token);
  setCookies(KEYS.AUTH_TOKEN, token);
};

export const destroySession: () => void = () => {
  delete API.defaults.headers.common['authorization'];
  removeItem(KEYS.AUTH_TOKEN);
  removeItem(KEYS.USER_PROFILE);
  deleteCookie(KEYS.AUTH_TOKEN);
  deleteCookie(KEYS.USER_ROLE);
};

type Page = number | string | string[];

export type PaginatedEndointParams = {
  page?: Page;
  pageSize?: number | string;
  promoted?: boolean;
  fieldsOfExpertiseId?: number | string;
  industry?: Page | string | null | undefined;
  name?: string;
  highschoolProfile?: Page | null | undefined;
  workModel?: Page | null | undefined;
  practiceType?: string | string[] | null | undefined;
};

type EmployerOfferParams = {
  slug: Page;
  page?: Page;
};

type MyOffersParams = {
  page?: Page;
};

type ApplicantsParams = {
  applicationStatus?: Page | null | undefined;
  offerType?: string | null | undefined;
  page?: Page;
  offer?: Page | null | undefined;
  ascending?: boolean;
  applicant?: string;
};

export const APIRoutes = {
  navigation: '/navigation',
  banners: (target: BannerTarget[]) => `/banners?target=${target.join(',')}`,
  staticPage: (target: number | string | string[]) => `/static_pages/${String(target)}`,
  offer: (id?: number | string | string[]) => (id ? `/offers/${String(id)}` : '/offers'),
  offers: (params?: PaginatedEndointParams) => {
    const {
      pageSize = 24,
      page = 1,
      promoted = null,
      highschoolProfile = null,
      industry = null,
      workModel = null,
      practiceType = null,
    } = params || {};

    const practiceTypeQuery = practiceType
      ? practiceType
          .toString()
          .split(',')
          .map((p) => `offer[practice_type][]=${p}`)
      : [];

    const queryParams = [
      ...(highschoolProfile ? [`offer[highschool_profiles][id]=${String(highschoolProfile)}`] : []),
      ...(industry ? [`offer[employer_profile][fields_of_expertise_id]=${String(industry)}`] : []),
      ...(workModel ? [`offer[work_model]=${String(workModel)}`] : []),
      ...(practiceType ? practiceTypeQuery : []),
      ...(promoted ? [`offer[promoted]=${String(promoted)}`] : []),
    ];

    return `/offers?page_size=${String(pageSize)}&page=${String(page)}${
      queryParams && queryParams.length ? `&${queryParams.join('&')}` : ''
    }`;
  },
  offerApply: (id: number) => `/offers/${id}/apply`,
  filters: '/filters',
  news: '/news',
  newsletter: '/newsletter_subscribers',
  employer: (slug: Page) => `/employer_profile/${String(slug)}`,
  candidate: (slug: Page) => `/student_profile/${String(slug)}`,
  employerOffers: (params?: EmployerOfferParams) => {
    const { page = 1, slug = null } = params || {};
    return `/employer_profile/${String(slug)}/offers?page=${String(page)}`;
  },
  quickSearch: (query: string) => `/offers/search_cues?name=${query}`,
  employers: (params?: PaginatedEndointParams) => {
    const { pageSize = 24, page = 1, promoted = null, fieldsOfExpertiseId = null, name = null } = params || {};

    const queryParams = [
      ...(promoted ? [`employer[promoted]=${String(promoted)}`] : []),
      ...(fieldsOfExpertiseId ? [`employer[fields_of_expertise_id]=${String(fieldsOfExpertiseId)}`] : []),
      ...(name ? [`employer[name]=${name}`] : []),
    ];

    return `/employers?page_size=${String(pageSize)}&page=${String(page)}${
      queryParams && queryParams.length ? `&${queryParams.join('&')}` : ''
    }`;
  },
  highschools: '/universities',
  highschoolProfiles: '/fields_of_study',
  fieldsOfExpertises: '/fields_of_expertise',
  languages: '/languages',
  auth: {
    signup: '/users',
    signin: '/users/sign_in',
    signout: '/users/sign_out',
    password: '/users/password',
    confirmAccount: (token: string | string[]) => `/users/confirmation?confirmation_token=${String(token)}`,
  },
  me: '/users/me',
  terms: '/users/me/terms',
  myOffers: (params?: MyOffersParams) => {
    const { page = 1 } = params || {};
    return `/users/me/offers?page=${String(page)}`;
  },
  studentApplications: (page = 1) => `/users/me/applications?page=${page}`,
  applicants: (params?: ApplicantsParams) => {
    const {
      applicationStatus = null,
      offerType = null,
      page = 1,
      offer = null,
      ascending = false,
      applicant = '',
    } = params || {};
    const applicationStatusQuery = applicationStatus ? `&application[status]=${String(applicationStatus)}` : '';
    const offerTypeQuery = offerType ? `&application[offers][practice_type]=${offerType}` : '';
    const offerQuery = offer ? `&application[offer_id]=${String(offer)}` : '';
    const sortQuery = `&sort=${ascending ? 'asc' : 'desc'}`;
    const studentName = applicant ? `&applicant=${applicant}` : '';

    const base = `/users/me/applicants?page=${String(page)}`;
    return `${base}${applicationStatusQuery}${offerTypeQuery}${offerQuery}${sortQuery}${studentName}`;
  },
  applicationCounts: '/users/me/applicants/counts',
  upadteStudentProfile: (id: number) => `/student_profile/${id}`,
  upadteEmployerProfile: (id?: number) => (id ? `/employer_profile/${id}` : '/employer_profile'),
  contact: '/contact',
  updateApplicationStatus: (offerId: number, applicationId: number) =>
    `offers/${offerId}/applications/${applicationId}`,
  batchUpdateApplicationStatus: '/users/me/applicants',
  offersOfLoggedEmployer: (name: string) => `/users/me/offer_cues?name=${name}`,
};
